import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { templatesCategoryConfig } from '@/pages/settings/ats/templates-settings/constants';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';
import { Box } from '@chakra-ui/react';
import { ChooseSurveyTemplate } from './ChooseSurveyTemplate';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { ScheduleForLater } from '@/components/ats/job-stage-actions/actions/assessment/send-assessment/schedule/ScheduleForLater';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { CenterSpinner } from '@/components/ats';

const getSurveyEmailInitialValue = (
  activity,
  selectedTemplate,
  variablesListArr,
  currUser,
  fromUsers
) => {
  const {
    candidate_comm_sender = undefined,
    cc_participants,
    bcc_participants,
    candidate_comm_template,
    ats_shared_email = undefined,
    to_participants = undefined,
  } = activity;

  // Saved `candidate_comm_sender` may point to a user whose
  // `email_delegation_enabled` was turned off after the activity was
  // configured. The BE's `users?scope=email_delegatable` list (and our
  // `fromUsers`) filters those out, so pre-filling their id leaves the
  // From dropdown showing a value that isn't a valid option. When that
  // happens we leave From empty so the required-field validation forces
  // the user to pick a valid sender before submit.
  const hasSavedSender = !!candidate_comm_sender?.id;
  const isSavedSenderDelegatable =
    hasSavedSender &&
    Array.isArray(fromUsers) &&
    fromUsers.some(
      (u) => !u?.isSharedEmail && u?.id === candidate_comm_sender.id
    );

  let senderIdForFrom: string | undefined;
  let senderObjectForFrom: any;
  if (hasSavedSender) {
    senderIdForFrom = isSavedSenderDelegatable
      ? candidate_comm_sender.id
      : undefined;
    senderObjectForFrom = isSavedSenderDelegatable
      ? candidate_comm_sender
      : undefined;
  } else {
    senderIdForFrom = currUser?.id;
    senderObjectForFrom = currUser;
  }

  const emailPayload = {
    from: ats_shared_email?.id || senderIdForFrom,
    fromUserObject: ats_shared_email?.id
      ? { ...ats_shared_email, isSharedEmail: true }
      : senderObjectForFrom,
    cc: cc_participants?.map((item) => item?.id),
    bcc: bcc_participants?.map((item) => item?.id),
    toAddress: to_participants?.map((item) => item?.id),
    variablesListArr,
  };

  if (!activity?.id || selectedTemplate?.id) {
    emailPayload['subject'] = selectedTemplate?.subject || '';
    emailPayload['body'] = selectedTemplate?.body || '';
    emailPayload['attachments'] = selectedTemplate?.attachments || [];
  } else {
    const { subject, body, attachments } = candidate_comm_template || {};
    emailPayload['subject'] = subject;
    emailPayload['body'] = body;
    emailPayload['attachments'] = attachments;
  }
  return emailPayload;
};

interface SurveyFormEditorProps {
  formRef?: any;
  selectedTemplate?: any;
  handleFormSubmit?: () => void;
  selectOnChange?: () => void;
  activity?: any;
  surveyType?: string;
  isScheduled?: boolean;
  isInterviewPlan?: boolean;
  scheduling?: {
    scheduleRef: any;
    handleSchedule: (_scheduleValues: any) => void;
    handleCancelSchedule: () => void;
    isScheduling: boolean;
    isCanceling: boolean;
  };
}

export const SurveyFormEditor: React.FC<SurveyFormEditorProps> = ({
  formRef = null,
  selectedTemplate: selectedTemplateProp = {},
  handleFormSubmit = () => {},
  selectOnChange = () => {},
  activity = {},
  surveyType = '',
  isScheduled = false,
  isInterviewPlan = false,
  scheduling = {
    scheduleRef: null,
    handleSchedule: () => {},
    handleCancelSchedule: () => {},
    isScheduling: false,
    isCanceling: false,
  },
}) => {
  // Derive effective template: use prop if available, otherwise fallback to activity data
  // Note: Activity object uses candidate_comm_template (mapped from API's ats_comm_template)
  const selectedTemplate = selectedTemplateProp?.id
    ? selectedTemplateProp
    : activity?.candidate_comm_template || {};
  const view = useMemo(() => {
    const baseView = templatesCategoryConfig.survey_notice.view;
    if (surveyType === 'employee') {
      return baseView;
    }
    return baseView.filter((item) => item !== 'user');
  }, [surveyType]);

  const { data: { variables: rawVariables = {} } = {} } =
    useGetTemplatesVariablesQuery({
      params: { view },
    });

  // Filter out user.email_signature from variables for survey context
  const variables = useMemo(() => {
    const filtered = { ...rawVariables };
    if (filtered.user && Array.isArray(filtered.user)) {
      filtered.user = filtered.user.filter(
        (variable: string) => variable !== 'user.email_signature'
      );
    }
    return filtered;
  }, [rawVariables]);

  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values
  );

  const users = useSelector(selectUsers);
  const currUser = useSelector(selectCurrUser);
  const { fromUsers, isFromUsersLoading } = useGetAllUsers();
  // include ToAddress field when survey type is employee
  const toAddress = useMemo(() => {
    if (surveyType === 'employee') {
      return users.map((user) => ({
        email: user.email,
        name: user.name,
        id: user.id,
        avatar: user.avatar,
      }));
    }
    return [];
  }, [surveyType, users]);

  const emailFieldConfigs = useEmailField({
    formRef,
    templateData: selectedTemplate,
    disableFrom: false,
    valueKey: 'id',
    variables,
    showInvalidVariableAlert: false,
    toAddress,
    ToAddressMultiSelect: true,
    showFromMandatoryIcon: !isInterviewPlan, // From is mandatory when NOT in interview plan (i.e., sending survey)
    showToMandatoryIcon: !isInterviewPlan, // To is mandatory when NOT in interview plan (i.e., sending survey)
  });

  // Get default template names based on survey type (array of possible names to match)
  const initialTemplateNames =
    surveyType === 'candidate'
      ? [
          'Candidate Survey Template',
          'Candidate survey email',
          'Candidate survey email template',
          'Candidate survey ',
          'Candidate survey template',
        ]
      : [
          'HM Survey Template',
          'HM survey email',
          'HM survey email template',
          'HM survey ',
          'Hiring Manager Survey Template',
        ];

  // In edit mode (activity.id exists), don't auto-select initial template
  const isEditMode = !!activity?.id;

  const surveyEmailFormConfig = [
    {
      type: 'custom',
      Field: ChooseSurveyTemplate,
      props: {
        selectOnChange,
        styleVersion: 'v2',
        category: ['survey_notice'],
        initialValue: isEditMode ? null : initialTemplateNames,
        surveyType: surveyType,
        selectedTemplate: selectedTemplate,
      },
    },
    {
      id: 'emailFormConfig',
      type: 'wrapper',
      fields: emailFieldConfigs.map((field) =>
        field?.name === 'from' && !isInterviewPlan
          ? { ...field, isRequired: true, errorMessage: 'From is required' }
          : field
      ),
      Wrapper: Box,
      wrapperProps: {
        border: '1px solid',
        borderColor: 'border-color',
        borderRadius: 'sm',
        marginTop: '12px',
      },
    },
    getInvalidVariableAlertConfig({
      variablesListArr,
      isBodySubject: true,
    }),
  ];

  // Wait for the delegatable-senders list before computing the initial
  // From value — otherwise the validation below sees an empty list and
  // always falls back to the current user, even when the saved sender
  // is actually still delegatable.
  if (isFromUsersLoading) {
    return <CenterSpinner flex="1" />;
  }

  return (
    <Box px="24px" pt="16px">
      <FormBuilder
        key={selectedTemplate?.id}
        initialValues={getSurveyEmailInitialValue(
          activity,
          selectedTemplate,
          variablesListArr,
          currUser,
          fromUsers
        )}
        config={surveyEmailFormConfig}
        onSubmit={handleFormSubmit}
        formRef={formRef}
      />
      {isScheduled && (
        <Box mb="80px">
          <ScheduleForLater
            scheduleRef={scheduling.scheduleRef}
            scheduleDetails={activity}
            onSchedule={(values) => scheduling.handleSchedule(values)}
            onCancelSchedule={scheduling.handleCancelSchedule}
            isScheduling={scheduling.isScheduling}
            isCanceling={scheduling.isCanceling}
            isScheduled={true}
            styles={{
              margin: '8px 0px !important',
            }}
          />
        </Box>
      )}
    </Box>
  );
};
